import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";

/* ── Global soundscape & audio governance ──────────────────────────────────
   Mounted once at the top of the app, OUTSIDE the router's page switch, so the
   ambience keeps flowing and crossfades seamlessly as the visitor moves between
   pages — one continuous journey rather than separate pages.

   Single source of truth for the whole audio experience: master volume, mute,
   ambient on/off, gentle fades, narration "ducking", and CHAPTER SCENES — each
   route carries a curated ambient mood, blended into the next with a two-deck
   crossfader so no two chapters ever start or stop abruptly. */

// Curated chapter moods. Each id maps to an ambient track.
export const SCENE_SRC = {
  reflection: "/audio/soundscape-v1.mp3",       // introspective forest — home & essays
  ocean: "/audio/ocean-soundscape-v8-pure.mp3", // nature & travel — the World. Homepage opening: trimmed self-hosted variant of ocean-soundscape-v8.mp3 that begins on the audible wave body (skips the ~1 s quiet fade-in that some listeners perceive as a wispy/musical intro). The original file is preserved on disk unchanged.
  monastery: "/audio/ambience-monastery.mp3",   // contemplative — the Journal
  chapter3: "/audio/ambience.mp3",               // Chapter Three montage — its own reflective soundscape (swap here for licensed music later)
  ambient: "/audio/ambience.mp3",               // soft neutral — resources / contact / Thriver
};
const DEFAULT_SCENE = "reflection";

// Which chapter each route belongs to.
const ROUTE_SCENE = {
  "/": "ocean",
  "/about": "reflection",
  "/world": "ocean",
  "/journal": "monastery",
  "/resources": "ambient",
  "/start-here": "ambient",
  "/contact": "ambient",
};
const sceneForPath = (path) => {
  if (ROUTE_SCENE[path]) return ROUTE_SCENE[path];
  if (path.startsWith("/stories")) return "reflection";
  if (path.startsWith("/explore")) return "reflection";
  if (path.startsWith("/foundations")) return "reflection";
  return DEFAULT_SCENE;
};

const ON_KEY = "ttl101-ambient-pref";   // session-scoped: never autostart on a fresh visit
const AUDIO_KEY = "ttl-audio";           // persisted across visits: volume + mute

const AMBIENT_CEIL = 0.55;   // ambient never exceeds this fraction of master volume
const DUCK_FACTOR = 0.18;    // ambient level while narration is speaking
const FADE_MS = 600;         // gentle ambient fade in / out
const SCENE_MS = 900;        // slightly longer, luxurious chapter crossfade

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

function loadAudioPrefs() {
  try {
    const raw = JSON.parse(localStorage.getItem(AUDIO_KEY) || "null");
    if (raw && typeof raw.masterVolume === "number") {
      return { masterVolume: raw.masterVolume, muted: !!raw.muted };
    }
  } catch (e) { /* ignore */ }
  const quiet = prefersReducedMotion();
  return { masterVolume: quiet ? 0.4 : 0.6, muted: quiet };
}

const AudioContext = createContext(null);

export function AudioProvider({ children }) {
  const location = useLocation();
  const deck0 = useRef(null);
  const deck1 = useRef(null);
  const activeIdx = useRef(0);        // which deck currently carries the scene
  const ramps = useRef({});
  const duckCount = useRef(0);
  const firstScene = useRef(true);

  const initial = loadAudioPrefs();
  // Restored original behaviour: the homepage opens with ambient sound-on by
  // default. The only way sound is off on arrival is if the visitor
  // explicitly turned it off earlier in this session (persisted as "off" in
  // sessionStorage by toggle()). This mirrors the pre-3a31650 mount policy —
  // just applied to the current chapter track (pure ocean) instead of the
  // retired forest track. Kept small: one call site.
  const [on, setOn] = useState(() => {
    try { return sessionStorage.getItem(ON_KEY) !== "off"; } catch (e) { return true; }
  });
  const [scene, setSceneState] = useState(sceneForPath(location.pathname));
  const [masterVolume, setMasterVolume] = useState(initial.masterVolume);
  const [muted, setMuted] = useState(initial.muted);
  const [ducking, setDucking] = useState(false);

  // Initial src for the mounted-once deck. Captured lazily on first render so
  // React writes the correct chapter track into the <audio> element straight
  // away (letting `preload="auto"` begin fetching the right file immediately
  // — critical on mobile, where any src-swap after mount defers the download
  // to the user's first tap and blows the "begin within one second" budget).
  // React never re-syncs this attribute on subsequent renders, so the
  // imperative crossfade code below remains the sole owner of src updates.
  const [initialDeck0Src] = useState(() => SCENE_SRC[sceneForPath(location.pathname)] || SCENE_SRC[DEFAULT_SCENE]);

  const deckEl = (i) => (i === 0 ? deck0.current : deck1.current);

  const effVol = useCallback(
    () => (on && !muted ? masterVolume * AMBIENT_CEIL * (ducking ? DUCK_FACTOR : 1) : 0),
    [on, muted, masterVolume, ducking]
  );

  useEffect(() => {
    try { localStorage.setItem(AUDIO_KEY, JSON.stringify({ masterVolume, muted })); } catch (e) {}
  }, [masterVolume, muted]);

  // Duration-based fade using requestAnimationFrame for buttery smoothness.
  const fadeTo = useCallback((el, key, rawTarget, ms = FADE_MS) => {
    if (!el) return;
    const target = Math.max(0, Math.min(1, rawTarget));
    cancelAnimationFrame(ramps.current[key]);
    if (target > 0 && el.paused) {
      const p = el.play();
      if (p && p.catch) p.catch(() => {});
    }
    const start = Math.max(0, Math.min(1, el.volume));
    if (Math.abs(start - target) < 0.001) {
      el.volume = target;
      if (target === 0 && !el.paused) el.pause();
      return;
    }
    const t0 = performance.now();
    const step = (now) => {
      const k = Math.min(1, (now - t0) / ms);
      el.volume = Math.max(0, Math.min(1, start + (target - start) * k));
      if (k < 1) {
        ramps.current[key] = requestAnimationFrame(step);
      } else {
        el.volume = target;
        if (target === 0) el.pause();
      }
    };
    ramps.current[key] = requestAnimationFrame(step);
  }, []);

  // Keep the active deck at the correct effective volume (idle deck stays silent).
  const applyActive = useCallback((ms = FADE_MS) => {
    const active = deckEl(activeIdx.current);
    fadeTo(active, `deck${activeIdx.current}`, effVol(), ms);
  }, [effVol, fadeTo]);

  useEffect(() => { applyActive(); }, [applyActive]);

  // Chapter crossfade: load the new mood into the idle deck, fade it up while the
  // outgoing chapter fades down — a seamless blend between chapters.
  useEffect(() => {
    if (firstScene.current) {
      firstScene.current = false;
      // On mount, kick off buffering of the initial scene track immediately so
      // that the visitor's first activation starts playback within one second
      // even on mobile browsers that treat `preload="auto"` as advisory. The
      // src itself is already correct — React wrote it from the useState-lazy
      // `initialDeck0Src` — so we only need to nudge the element to start
      // fetching (idempotent; safe to call even if already buffered).
      const active = deckEl(activeIdx.current);
      if (active) {
        try { active.load(); } catch (e) { /* ignore */ }
      }
      return;
    }
    const src = SCENE_SRC[scene] || SCENE_SRC[DEFAULT_SCENE];
    const fromIdx = activeIdx.current;
    const toIdx = 1 - fromIdx;
    const to = deckEl(toIdx);
    const from = deckEl(fromIdx);
    if (!to) return;
    if (to.getAttribute("src") !== src) { to.setAttribute("src", src); to.load(); }
    to.currentTime = 0;
    to.volume = 0;
    activeIdx.current = toIdx;
    fadeTo(to, `deck${toIdx}`, effVol(), SCENE_MS);
    if (from) fadeTo(from, `deck${fromIdx}`, 0, SCENE_MS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scene]);

  // Route → chapter mapping (only on pathname change; in-page overrides win between changes).
  // Homepage-only audio policy: the ambient soundscape belongs to the homepage
  // hero and MUST NOT leak to any other page. Whenever the visitor navigates
  // away from "/", we immediately silence both decks, reset them to the start
  // and turn the ambient off. Returning to "/" does not auto-resume — the
  // visitor must deliberately re-activate sound with the on-screen control.
  useEffect(() => {
    const isHome = location.pathname === "/";
    setSceneState(sceneForPath(location.pathname));
    if (!isHome) {
      // Force off + hard-stop both decks immediately (no fade). Do NOT clear
      // the ON_KEY here — an explicit "off" the visitor set on the homepage
      // must persist across navigation so the ocean does not restart on their
      // return. This useEffect only silences the current, transient "on"
      // state; the session preference is owned exclusively by toggle().
      setOn(false);
      Object.values(ramps.current).forEach((id) => cancelAnimationFrame(id));
      [deck0.current, deck1.current].forEach((el) => {
        if (!el) return;
        try {
          el.pause();
          el.volume = 0;
          el.currentTime = 0;
        } catch (e) { /* ignore */ }
      });
    }
  }, [location.pathname]);

  // Restored original opening: on the homepage, attempt to start the ambient
  // deck immediately from t=0. The default `on` state (above) is already
  // true, so the crossfade "applyActive" effect will call play() through the
  // fade; we additionally issue a direct play() here to make the attempt
  // deterministic on first mount.
  //
  // If the browser blocks audible autoplay we flip `on` back to false so the
  // collapsed SoundToggle reverts to its "EXPERIENCE WITH SOUND" affordance —
  // a single tap then triggers `toggle()` (a genuine user gesture), which
  // fades the ambient in and plays audibly. Without this fallback the button
  // would remain in its icon-only "on" state and require the visitor to open
  // the panel and click the ambient sub-toggle just to start playback.
  useEffect(() => {
    if (location.pathname !== "/") return;
    let cancelled = false;
    try { if (sessionStorage.getItem(ON_KEY) === "off") return; } catch (e) {}
    const active = deckEl(activeIdx.current);
    if (!active) return;
    const p = active.play();
    if (p && typeof p.then === "function") {
      p.catch(() => { if (!cancelled) setOn(false); });
    }
    return () => { cancelled = true; };
  }, [location.pathname]);

  const setScene = useCallback((id) => {
    if (SCENE_SRC[id]) setSceneState(id);
  }, []);

  const toggle = useCallback(() => {
    setOn((prev) => {
      const next = !prev;
      // Aman-style session policy: persist ONLY an explicit "off" so that the
      // homepage stops autoplaying the ocean for the rest of the session once
      // the visitor turns the sound off. Turning it back on clears that flag.
      try {
        if (next) sessionStorage.removeItem(ON_KEY);
        else sessionStorage.setItem(ON_KEY, "off");
      } catch (e) {}
      return next;
    });
  }, []);

  const toggleMute = useCallback(() => setMuted((m) => !m), []);

  const changeVolume = useCallback((v) => {
    const clamped = Math.max(0, Math.min(1, v));
    setMasterVolume(clamped);
    if (clamped > 0 && muted) setMuted(false);
  }, [muted]);

  const duck = useCallback(() => { duckCount.current += 1; setDucking(true); }, []);
  const unduck = useCallback(() => {
    duckCount.current = Math.max(0, duckCount.current - 1);
    if (duckCount.current === 0) setDucking(false);
  }, []);

  const narrationVolume = muted ? 0 : masterVolume;

  useEffect(() => {
    // Ambient audio is homepage-only and never auto-resumes on a fresh visit
    // or on return to the homepage. The visitor must deliberately activate it
    // each time using the on-screen sound control.
    const r = ramps.current;
    return () => Object.values(r).forEach((id) => cancelAnimationFrame(id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AudioContext.Provider
      value={{
        on, toggle,
        muted, toggleMute,
        masterVolume, setVolume: changeVolume,
        scene, setScene,
        ducking, duck, unduck,
        narrationVolume,
      }}
    >
      {/* Two persistent crossfade decks — never remounted on navigation.
          deck0 renders its initial src from the current route's scene so the
          browser can start preloading the correct chapter track before the
          visitor's first interaction. From that point on, the crossfade
          effect owns every src change imperatively (via setAttribute); React
          does not re-sync this attribute because `initialDeck0Src` is
          captured once and never changes. */}
      <audio ref={deck0} src={initialDeck0Src} loop preload="auto" data-testid="ambient-audio" />
      <audio ref={deck1} loop preload="auto" data-testid="ambient-audio-b" />
      {children}
    </AudioContext.Provider>
  );
}

export function useAudio() {
  const ctx = useContext(AudioContext);
  if (!ctx) throw new Error("useAudio must be used within AudioProvider");
  return ctx;
}
